// @wc-ignore-file
// CR-273: pure client-side rules for the Notion page summaries shown on a CR.
// Every string in here is DATA (summary statuses), never UI copy — the file is
// excluded from wuchale extraction so a non-English locale cannot translate a
// status and break the comparisons below.

/** How often the CR page re-reads its Notion references while a summary is pending (D10). */
export const CR_NOTION_SUMMARY_POLL_INTERVAL_MS = 10_000;

/**
 * How long after an attach / Update the list keeps polling. The cron worker
 * picks a pending row up within about a minute; one still pending after five
 * minutes is waiting on a backoff or a stopped worker, and polling it forever
 * would keep every open CR page issuing a request every ten seconds.
 */
export const CR_NOTION_SUMMARY_POLL_WINDOW_MS = 5 * 60 * 1_000;

/**
 * The sentence the worker stores for a page with no readable text (keep in
 * sync with EMPTY_PAGE_SUMMARY in backend/api-server/src/lib/notion/notion-summary.ts).
 * It is English whatever the page language, so the UI shows its own
 * translated wording instead.
 */
export const CR_NOTION_EMPTY_PAGE_SUMMARY = "The page has no readable content.";

/**
 * `true` when the stored summary only says the page has no readable content.
 * The worker stores the sentence whenever the page text is empty; the block
 * count of 0 is the same case for a page with no blocks at all.
 */
export function isCrNotionEmptyPageSummary(reference: {
  summary_text: string | null;
  summary_content_block_count: number | null;
}): boolean {
  // `== null`: an older cached row (or a test fixture) may lack the column.
  if (reference.summary_text == null) return false;
  return (
    reference.summary_content_block_count === 0 ||
    reference.summary_text.trim() === CR_NOTION_EMPTY_PAGE_SUMMARY
  );
}

/** Structural shape so this stays free of the API layer's types (no import cycle). */
type PollingReference = {
  summary_status: string;
  updated_at: string;
};

/**
 * `true` while THIS reference waits for its summary and was attached or
 * updated recently enough to be worth watching. Measured from `updated_at`
 * (set by the attach and by the Update re-queue) rather than from React state,
 * so a reload or a second viewer sees the same window. A date Date.parse
 * rejects yields NaN, which fails the comparison: polling stops, the safe side.
 * A client clock running more than 5 min ahead of the server never polls
 * (the summary then shows on the next visit or refetch) — accepted.
 */
export function isCrNotionSummaryInPollWindow(
  reference: PollingReference,
  now: number = Date.now(),
): boolean {
  if (reference.summary_status !== "pending") return false;
  return now - Date.parse(reference.updated_at) < CR_NOTION_SUMMARY_POLL_WINDOW_MS;
}

/** `refetchInterval` for the CR page's Notion references query. */
export function crNotionReferencesRefetchInterval(
  references: readonly PollingReference[] | undefined,
  now: number = Date.now(),
): number | false {
  return (references ?? []).some((reference) => isCrNotionSummaryInPollWindow(reference, now))
    ? CR_NOTION_SUMMARY_POLL_INTERVAL_MS
    : false;
}
